import { Message } from "../../../types/Entities/Messaging/Message";
import { Box, Rating, Stack } from "@mui/material";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import messageCenterMessageStyle from "./messageCenterMessageStyle";
import DownloadTile from "../../PetitionSummary/Documents/DownloadTile";
import { useState } from "react";
import { apiPatch, apiPost } from "../../../services/apiClient";
import { queryKeys } from "../../../services/reactQuery/reactQueryService";
import { useQueryClient } from "@tanstack/react-query";
import { CustomerFeedback } from "../../../types/Entities/CustomerFeedback";
import { MinimalCase } from "../../../types/Entities/MinimalCase";
import { useTheme } from "@mui/material/styles";
import ButtonLoading from "../../Button/ButtonLoading";
import dayjs from "dayjs";

function MessageCenterMessage({ message, product }: { message: Message; product: MinimalCase }) {
  const isCustomerMessage = message.senderType === "customer";
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [rating, setRating] = useState<number | null>(message.customerFeedback?.stars ?? null);
  const theme = useTheme();

  const submitFeedback = async () => {
    if (!rating) {
      return;
    }
    setIsLoading(true);
    if (message.customerFeedback) {
      await apiPatch(`/customer_feedbacks/${message.customerFeedback.id}`, {
        stars: rating,
      });
    } else {
      const feedback: CustomerFeedback = await apiPost("/customer_feedbacks", {
        stars: rating,
        source: "message",
        case: `/abstract_cases/${product.id}`,
      });
      await apiPatch(`/messages/${message.id}`, { customerFeedback: `/customer_feedbacks/${feedback.id}` });
    }
    await queryClient.invalidateQueries({
      queryKey: queryKeys.collection("/conversations"),
    });
    setIsLoading(false);
  };

  return (
    <>
      <Box
        sx={Object.assign(
          {},
          messageCenterMessageStyle.messageContainer,
          isCustomerMessage ? messageCenterMessageStyle.ownMessageContainer : messageCenterMessageStyle.otherMessageContainer
        )}
      >
        <Stack
          direction={"row"}
          sx={{
            display: "flex",
            justifyContent: "flex-end"
          }}>
          <Typography>{dayjs(message.created).format("DD.MM.YYYY, HH:mm") + " Uhr"}</Typography>
        </Stack>
        <Paper
          elevation={0}
          sx={Object.assign(
            {},
            messageCenterMessageStyle.messageBubble,
            isCustomerMessage ? messageCenterMessageStyle.ownMessageBubble(theme) : messageCenterMessageStyle.otherMessageBubble(theme)
          )}
        >
          <Typography
            sx={{
              fontWeight: "bold",
              color: "primary.main"
            }}>
            {message.senderType === "system" ? "Legalbird" : message.sender.messagingUserFullName}
          </Typography>
          <Typography dangerouslySetInnerHTML={{ __html: message.messageText }} sx={messageCenterMessageStyle.ownMessageText} />
          {message.attachments?.length > 0 && (
            <Grid container spacing={3} sx={{
              justifyContent: "flex-start"
            }}>
              {message.attachments.map((mediaObject) => (
                <Grid key={mediaObject.id}>
                  <DownloadTile mediaObject={mediaObject} size={"small"} />
                </Grid>
              ))}
            </Grid>
          )}
        </Paper>
        {!isCustomerMessage && message.showCustomerFeedback && (
          <Box sx={messageCenterMessageStyle.customerFeedbackBox}>
            <Typography color={"textSecondary"} sx={{
              fontSize: "small"
            }}>
              {message.customerFeedback ? "Vielen Dank für Ihre Bewertung." : "Wie hilfreich war diese Nachricht für Sie?"}
            </Typography>
            <Stack direction={"row"} spacing={2} sx={{
              alignItems: "center"
            }}>
              <Rating
                name="simple-controlled"
                value={rating ?? 0}
                disabled={isLoading || !!message.customerFeedback}
                onChange={async (event, stars) => setRating(stars)}
              />
              {rating && !message.customerFeedback && (
                <ButtonLoading size={"small"} isLoading={isLoading} onClick={submitFeedback}>
                  Bewertung abschicken
                </ButtonLoading>
              )}
            </Stack>
          </Box>
        )}
      </Box>
    </>
  );
}

export default MessageCenterMessage;
